<template>
  <v-container fluid>
    <v-row>
      <v-col cols="4">
        <v-autocomplete label="Please select a user" v-model="coachId" dense :items="staff" @change="changeCoachId" />
      </v-col>
    </v-row>
    <v-row>
      <v-col cols="12">
        <v-data-table
          :headers="headers"
          :items="userSubscriptions"
          :loading="loading || $apollo.loading"
          :search="search"
          :show-group-by="true"
          class="mt-2 elevation-2"
        >
          <template v-slot:top>
            <v-text-field v-model="search" class="mx-4 mt-0 pt-0">
              <template v-slot:label>
                <v-icon color="secondary"> mdi-magnify </v-icon>
                Search
              </template>
            </v-text-field>
          </template>
          <template v-slot:[`header.userName`]="props">
            <table-header :text="props.header.text" :tooltip="props.header.help" />
          </template>
          <template v-slot:[`item.userName`]="props">
            <a
              :href="'/clients/' + props.item.user.id"
              target="_blank"
              class="secondary--text text-body-1 text-decoration-none"
            >
              {{ props.item.userName }}
            </a>

            <v-btn icon @click="copyEmail(props.item.userName, props.item.user.email)" class="my-auto">
              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                  <div v-bind="attrs" v-on="on" slot="activator">
                    <v-icon :color="copyEmailText == `Copied ${props.item.userName}'s email` ? 'green' : null"
                      >mdi-content-copy</v-icon
                    >
                  </div>
                </template>
                <span>{{
                  copyEmailText == `Copied ${props.item.userName}'s email` ? copyEmailText : "Copy email"
                }}</span>
              </v-tooltip>
            </v-btn>
          </template>
          <template v-slot:[`header.endDate`]="props">
            <table-header :text="props.header.text" :tooltip="props.header.help" />
          </template>
          <template v-slot:[`item.endDate`]="props">
            <span class="d-block">{{ props.item.endDate | moment("DD MMM YYYY") }}</span>
            <span class="font-weight-bold">{{ props.item.endDate | moment("from") }}</span>
          </template>
          <template v-slot:[`header.duration`]="props">
            <table-header :text="props.header.text" :tooltip="props.header.help" />
          </template>
          <template v-slot:[`item.duration`]="props">
            {{ props.item.duration }}
          </template>
          <template v-slot:[`header.productName`]="props">
            <table-header :text="props.header.text" :tooltip="props.header.help" />
          </template>
          <template v-slot:[`item.productName`]="props">
            <span :class="{ 'red--text': props.item.productName == 'None' }">{{ props.item.productName }}</span>
          </template>
          <template v-slot:[`header.likelyToReturn`]="props">
            <table-header :text="props.header.text" :tooltip="props.header.help" />
          </template>
          <template v-slot:[`item.likelyToReturn`]="props">
            <div class="text-center">
              <div v-if="props.item.likelyToReturn == true" class="green--text font-22">
                ✔
              </div>
              <div v-else-if="props.item.likelyToReturn == false" class="font-20">
                ✖
              </div>
              <v-icon v-else>mdi-minus</v-icon>
            </div>
          </template>
          <template v-slot:[`header.followUpDate`]="props">
            <table-header :text="props.header.text" :tooltip="props.header.help" />
          </template>
          <template v-slot:[`item.followUpDate`]="props">
            <span class="font-weight-bold" :class="{ 'red--text': props.item.pastFollowUpDate }">{{
              props.item.followUpDate | moment("ddd, DD MMM YYYY")
            }}</span>
          </template>
          <template v-slot:[`header.notesOnWhyLeft`]="props">
            <table-header :text="props.header.text" :tooltip="props.header.help" />
          </template>
          <template v-slot:[`header.notes`]="props">
            <table-header :text="props.header.text" :tooltip="props.header.help" />
          </template>
          <template v-slot:[`item.notes`]="props">
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  icon
                  elevation="13"
                  color="secondary"
                  class="ma-1 accent lighten-5"
                  dark
                  v-bind="attrs"
                  v-on="on"
                  @click="openNotes(props.item.user.id)"
                >
                  <v-icon>mdi-notebook</v-icon>
                </v-btn>
              </template>
              <span>Notes</span>
            </v-tooltip>
          </template>
          <template v-slot:[`header.birthday`]="props">
            <table-header :text="props.header.text" :tooltip="props.header.help" />
          </template>
          <template v-slot:[`item.birthday`]="props">
            {{ props.item.birthday | moment("DD MMM") }}
            <birthday-chip v-if="props.item.birthday" :date-of-birth="props.item.birthday.toString()" />
          </template>
          <template v-slot:[`header.lastActive`]="props">
            <table-header :text="props.header.text" :tooltip="props.header.help" />
          </template>
          <template v-slot:[`item.lastActive`]="props">
            {{ props.item.lastActive | moment("DD MMM YYYY HH:MM") }}
          </template>
          <template v-slot:[`header.currentPoints`]="props">
            <table-header :text="props.header.text" :tooltip="props.header.help" />
          </template>
          <template v-slot:[`item.currentPoints`]="props">
            <div @click="openPoints(props.item.user.id)" class="text-center font-weight-bold pointer grey-navy--text">
              {{ props.item.currentPoints }}
            </div>
          </template>
          <template v-slot:[`header.lastContactMade`]="props">
            <table-header :text="props.header.text" :tooltip="props.header.help" />
          </template>
          <template v-slot:[`item.lastContactMade`]="props">
            <span class="d-block">{{ props.item.lastContactMade | moment("DD MMM YYYY") }}</span>
            <span class="font-weight-bold">{{ props.item.lastContactMade | moment("from") }}</span>
          </template>
          <template v-slot:[`header.hubspotLink`]="props">
            <table-header :text="props.header.text" :tooltip="props.header.help" />
          </template>
          <template v-slot:[`item.hubspotLink`]="props">
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  icon
                  elevation="13"
                  color="secondary"
                  class="ma-1 accent lighten-5"
                  dark
                  v-bind="attrs"
                  v-on="on"
                  :href="props.item.hubspotLink"
                  target="_blank"
                >
                  <v-icon>mdi-hubspot</v-icon>
                </v-btn>
              </template>
              <span>Hubspot Link <v-icon color="white">mdi-open-in-new</v-icon></span>
            </v-tooltip>
          </template>
        </v-data-table>
      </v-col>
    </v-row>

    <v-dialog v-model="showNotes" class="limited-height" width="900" transition="dialog-bottom-transition">
      <user-notes
        v-if="showNotes"
        :userId="selectedUserId"
        :coachId="coachId"
        v-on:problem="emitProblem()"
        @close="closeNotes()"
      />
    </v-dialog>

    <v-dialog v-model="showPoints" width="900" class="limited-height" transition="dialog-bottom-transition">
      <client-reward-point-events :userId="selectedUserId" />
    </v-dialog>
  </v-container>
</template>

<script>
import { CLIENT_COACH_HISTORY_QUERY } from "@/constants/graphql/operations/onboarding";
import { STAFF_QUERY } from "@/constants/graphql/users/user";
import { getUserName } from "@/lib/username";
import BirthdayChip from "@/components/dashboard/components/BirthdayChip";
import UserNotes from "@/components/dashboard/components/notes/UserNotes";
import ClientRewardPointEvents from "@/components/client/blocks/activity/ClientRewardPointEvents";
import TableHeader from "@/components/dashboard/components/TableHeader";
import moment from "moment";

export default {
  name: "CoachClientHistory",
  components: {
    BirthdayChip,
    UserNotes,
    ClientRewardPointEvents,
    TableHeader,
  },
  data() {
    return {
      loading: true,
      coachId: this.$route.query.cId ?? this.$store.getters.user.id,
      userSubscriptions: [],
      selectedUserId: null,
      showNotes: false,
      showPoints: false,
      staff: [],
      search: "",
      showUserList: [],
      headers: [
        { text: "Client", value: "userName", groupable: false },
        { text: "Sub end date", value: "endDate", groupable: false, help: "Date this subscription ended" },
        { text: "Duration on past sub", value: "duration", groupable: false },
        { text: "Current subscription", value: "productName" },
        { text: "Likely to return to 1-on-1", value: "likelyToReturn", groupable: false },
        {
          text: "Follow up date",
          value: "followUpDate",
          groupable: false,
          help: "Find follow up reminder details in notes",
        },
        { text: "Notes on leaving", value: "notesOnWhyLeft", groupable: false },
        { text: "Notes", value: "notes", groupable: false },
        { text: "Birthday", value: "birthday", groupable: false },
        { text: "Last activity in web app", value: "lastActive", groupable: false },
        { text: "Current rewards points", value: "currentPoints", groupable: false },
        {
          text: "Last contact made",
          value: "lastContactMade",
          groupable: false,
          help: "Date pulled from last note taken for client",
        },
      ],
      copyEmailText: "Copy email",
    };
  },
  filters: {
    // data table does not sort when value is already a moment object
    moment(value, format = null) {
      if (value) {
        switch (format) {
          case null:
            return moment(value);
          case "from":
            return moment(value).fromNow();
          default:
            return moment(value).format(format);
        }
      }
      return "";
    },
  },
  methods: {
    async copyEmail(username, email) {
      await navigator.clipboard.writeText(email);
      this.copyEmailText = `Copied ${username}'s email`;
    },
    getName(user) {
      getUserName(user);
    },
    getId(base64string) {
      let decoded = atob(base64string).split(":");
      if (decoded[1]) return decoded[1];
      return "";
    },
    openNotes(userId) {
      this.selectedUserId = userId;
      this.showNotes = true;
    },
    openPoints(userId) {
      this.selectedUserId = userId;
      this.showPoints = true;
    },
    closeNotes() {
      this.showNotes = false;
    },
    duration(start, end) {
      start = this.$moment(start);
      end = this.$moment(end);
      return this.$moment.duration(end.diff(start)).humanize();
    },
    changeCoachId() {
      if (this.$route.query.cId) this.$router.replace({ query: null });
    },
  },
  apollo: {
    userSubscriptions: {
      query: CLIENT_COACH_HISTORY_QUERY,
      variables() {
        let encoded_json_payload = btoa("UserNode:" + this.coachId);
        return {
          coachId: encoded_json_payload,
          orderBy: "serviceStartDate",
        };
      },
      fetchPolicy: "cache-and-network",
      skip() {
        return !this.coachId;
      },
      result(response) {
        if (response.data) {
          const userList = [];
          response.data.userSubscriptions.edges.forEach((data) => {
            const obj = {
              userName: `${data.node.user?.firstName} ${data.node.user?.lastName}`,
              productName:
                data.node.user.subscriptions.edges.length > 0
                  ? data.node.user.subscriptions.edges[0].node.product.name
                  : "None",
              user: data.node.user,
              startDate: data.node.startDate,
              endDate: data.node.endDate,
              duration: this.duration(data.node.startDate, data.node.endDate),
              birthday: data.node.user.userProfile?.dateOfBirth,
              followUpDate: data.node.earliestIncompletefollowUpDate ?? null,
              pastFollowUpDate: this.$moment().isAfter(this.$moment(data.node.earliestIncompletefollowUpDate)),
              notesOnWhyLeft: data.node.notesOnWhyLeft,
              likelyToReturn: data.node.likelyToReturnOneOnOne,
              lastActive: data.node.user.userProfile?.lastActive,
              currentPoints: data.node.user.rewards?.totalPoints ?? 0,
              lastContactMade: data.node.user.userProfile?.lastNoteDate,
              hubspotLink:
                `https://app.hubspot.com/contacts/6239229/contact/${data.node.user.userProfile?.hubspotContactVid}` ??
                null,
            };
            userList[data.node.user.id] = obj;
          });
          this.userSubscriptions = userList;
          this.loading = false;
        }
      },
    },
    users: {
      query: STAFF_QUERY,
      variables() {
        return {
          isActive: true,
          isStaff: true,
        };
      },
      fetchPolicy: "cache-and-network",
      result(response) {
        if (response.data && !response.loading) {
          this.staff = [];

          response.data.users.edges.forEach((item) => {
            let data = {
              value: this.getId(item.node.id),
              text: getUserName(item.node),
            };

            this.staff.push(data);
          });

          this.staff.sort((a, b) => (a.text > b.text ? 1 : -1));
        }
        if (response.error && response.error.gqlError?.message == "Permission Denied.") {
          this.$router.push("/no-access");
        }
      },
    },
  },
};
</script>

<style lang="scss" scoped>
.font-20 {
  font-size: 20px;
}
.font-22 {
  font-size: 22px;
}
</style>
